import React, { useEffect, useState, useCallback } from 'react'
import { SELECT_COMPANY_PATH } from '../../lib/http'
import { Link, useLocation } from "react-router-dom"
import { useSelector } from "react-redux"
import { Switch } from "@mui/material"

import { StorageGetItem, USER } from "../../lib/storage"
import { useIsSupervisor } from "../../hooks/user"
import { useThemeContext } from "../../theme/ThemeContext"
import { logout } from "../../features/auth/logout"
import PopupWithActions from "../Popup/PopupWithAction"
import { useOfficeVisibility } from "../../hooks/useOfficeVisibility"
import { useTeamManager } from "../../hooks/useTeamManager"
import { useActiveBrand } from "../../hooks/useActiveBrand"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { AFFILIATE_TASKS_UI, USER_EMAIL_SETTINGS } from "../../config/featureFlagKeys"
import EmailSettingsDialog from "../EmailSettings/EmailSettingsDialog"
import noAvatar from "../../assets/images/icons/no_avatar.jpg"

const NAV_COLLAPSED_KEY = 'vlgNavCollapsed'
const EMAIL_SETTINGS_ROLES = ['customer', 'user', 'affiliate', 'affiliateGroupsAdmin']

// Inline stroke glyphs so each item gets a distinct, theme-colored icon that inherits
// the sidebar's text color for hover/active states. This also resolves the old top-nav
// icon collision where Accounting and Payment Processing both used Paid.svg.
const ICONS = {
  dashboard: '<rect x="3" y="3" width="7" height="9" rx="1.5"/><rect x="14" y="3" width="7" height="5" rx="1.5"/><rect x="14" y="12" width="7" height="9" rx="1.5"/><rect x="3" y="16" width="7" height="5" rx="1.5"/>',
  leads: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>',
  customers: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  tasks: '<path d="M9 11l3 3 8-8"/><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9"/>',
  payments: '<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>',
  accounting: '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/>',
  affiliates: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>',
  groupAdmins: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  history: '<path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/>',
  chevron: '<polyline points="15 18 9 12 15 6"/>',
  caret: '<polyline points="6 9 12 15 18 9"/>',
}

const Glyph = ({ name, className }) => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: ICONS[name] }}
  />
)

// Mirrors the role label the Header used to show next to the avatar.
const roleLabelFor = (role) => {
  switch (role) {
    case 'customer': return 'CRM Admin'
    case 'user': return 'User'
    case 'contact': return 'Client Portal'
    default: return role ? role.charAt(0).toUpperCase() + role.slice(1) : ''
  }
}

const AppSidebar = () => {
  const location = useLocation()
  const isSupervisor = useIsSupervisor()
  const { mode, toggleColorMode } = useThemeContext()
  const brand = useActiveBrand()
  const affiliateTasksOn = useSelector(selectFeatureFlag(AFFILIATE_TASKS_UI))
  const emailSettingsOn = useSelector(selectFeatureFlag(USER_EMAIL_SETTINGS))
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false)
  const [failedImages, setFailedImages] = useState([])
  const [user, setUser] = useState(null)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSED_KEY) === '1'
    } catch (error) {
      return false
    }
  })
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const loadable = (url) => (url && !failedImages.includes(url) ? url : null)
  const firmMark = loadable(brand?.markUrl)
  const firmLogo = collapsed ? firmMark : (firmMark || loadable(brand?.logoUrlDark))
  const firmLogoSize = collapsed ? 'w-8 h-8' : (firmLogo === firmMark ? 'w-9 h-9' : 'h-8 w-auto max-w-[96px]')
  const activeOfficeId = localStorage.getItem('activeOfficeId')
  const { offices, level: officeVisibilityLevel, resolved: officeVisibilityResolved } = useOfficeVisibility(user)
  const { canManageTeam } = useTeamManager(user)

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const result = await StorageGetItem(USER)
        setUser(result || null)
      } catch (error) {
        console.error('Error fetching user:', error)
      }
    }

    fetchUser()
  }, [])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0')
      } catch (error) {
        // localStorage can be unavailable (private mode) — collapse still works in-session.
      }
      return next
    })
    setMenuOpen(false)
    setMenuAnchor(null)
  }, [])

  // Cmd/Ctrl-B toggles the sidebar, matching the common editor shortcut.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.key !== 'b' && event.key !== 'B') return
      // Cmd/Ctrl-B is "bold" inside any editable field, so the field keeps it. closest()
      // rather than a tagName check because in the notes rich-text editor the event target
      // is a node *inside* the contenteditable host, not the host itself.
      const editable = event.target?.closest?.('input, textarea, select, [contenteditable]')
      if (editable && editable.getAttribute('contenteditable') !== 'false') return
      event.preventDefault()
      toggleCollapsed()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [toggleCollapsed])

  const openMenu = (event) => {
    setMenuAnchor(event.currentTarget)
    setMenuOpen(true)
  }
  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuOpen(false)
  }

  const switchOffice = (officeId) => {
    if (officeId === activeOfficeId) return closeMenu()

    localStorage.setItem('activeOfficeId', officeId)
    // A full reload rather than a refetch: every office-scoped slice already in the store
    // holds the previous office's data, and there is no single place to invalidate them.
    window.location.reload()
  }

  const userRole = user?.role || ''
  const showEmailSettings = emailSettingsOn && EMAIL_SETTINGS_ROLES.includes(userRole)
  const isGroupOwner = !!user?.isGroupOwner
  const isCustomer = userRole === 'customer'
  // An affiliate's accessLevel is the explicit statement of what they may reach, and
  // 'fullContactsNoAccounting' means exactly that: full contacts, NO accounting. Owning a contact
  // group must not silently override it (live 2026-08-05 — five affiliates with this level were
  // seeing Accounting because the gate keyed on isGroupOwner alone). The server enforces the same
  // rule on /payments/accounting/* — this only hides the entry point.
  const deniedAccounting = user?.accessLevel === 'fullContactsNoAccounting'
  const dashboardHidden = officeVisibilityResolved && officeVisibilityLevel === 'HIDDEN'
  const displayName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Account'
  const roleLabel = roleLabelFor(userRole)

  // Grouped IA. `show` mirrors the exact role gating from the previous top nav (NavHeader)
  // one-to-one so no role gains or loses access. Groups with no visible items are hidden.
  const groups = [
    {
      key: 'home',
      label: null,
      items: [
        { text: 'Dashboard', icon: 'dashboard', to: '/', show: !dashboardHidden && (isCustomer || isSupervisor || userRole === 'affiliate' || userRole === 'affiliateGroupsAdmin' || userRole === 'user') },
      ],
    },
    {
      key: 'work',
      label: 'Work',
      items: [
        { text: 'Leads', icon: 'leads', to: '/leads', show: true },
        { text: 'Customers', icon: 'customers', to: '/customers', show: true },
        { text: 'Tasks', icon: 'tasks', to: '/tasks', show: affiliateTasksOn || (userRole !== 'affiliate' && userRole !== 'affiliateGroupsAdmin') },
      ],
    },
    {
      key: 'finance',
      label: 'Finance',
      items: [
        // Payment Processing sits above Accounting (per product decision). Restricted to
        // internal MDL admins only (customer = Dolly's group, admin = super/dev).
        { text: 'Payment Processing', icon: 'payments', to: '/payments/daily-processing', match: '/payments', show: isCustomer || userRole === 'admin' },
        { text: 'Accounting', icon: 'accounting', to: '/accounting', show: isCustomer || isSupervisor || userRole === 'affiliateGroupsAdmin' || (userRole === 'affiliate' && isGroupOwner && !deniedAccounting) },
      ],
    },
    {
      key: 'people',
      label: 'People & Access',
      items: [
        { text: 'Users', icon: 'users', to: '/users', show: isCustomer },
        { text: 'Affiliates', icon: 'affiliates', to: '/affiliates', show: isCustomer },
        { text: 'Group Admins', icon: 'groupAdmins', to: '/affiliate-groups-admins', show: isCustomer },
        { text: 'My Team', icon: 'affiliates', to: '/my-team', show: canManageTeam },
      ],
    },
    {
      key: 'system',
      label: 'System',
      items: [
        { text: 'Settings', icon: 'settings', to: '/settings', show: isCustomer },
        { text: 'History', icon: 'history', to: '/history', show: isCustomer || userRole === 'user' },
      ],
    },
  ]

  const isItemActive = (item) => {
    const { pathname } = location
    if (item.to === '/') return pathname === '/'
    const base = item.match || item.to
    return pathname === base || pathname.startsWith(`${base}/`)
  }

  return (
    <nav
      aria-label="Primary"
      className={`${collapsed ? 'w-[68px]' : 'w-60'} shrink-0 self-start sticky top-0 h-screen bg-sidebar text-primary-contrast border-r border-[color:var(--color-sidebar-border)] flex flex-col transition-[width] duration-200 ease-in-out`}
    >
      {/* Brand (home link) + collapse toggle. When collapsed, the mark shows on the rail and
          hovering/focusing it reveals the expand control in its place.

          The firm named here is the one this browser is ACTING AS, not the one that shipped the
          build. It was the incumbent's name in literal text, which a person working in the second
          brand read as the truth. Nothing is named until the server answers, and a firm that owns no
          mark gets none — the same rule the letters follow, for the same reason.

          brightness-0 invert renders whatever mark arrives as a white silhouette, so a brand logo
          drawn for a light background still reads on the dark rail. */}
      <div className={`group relative flex items-center h-[64px] shrink-0 ${collapsed ? 'justify-center' : 'px-3 gap-2'}`}>
        <Link
          to="/"
          aria-label={brand?.name ? `${brand.name} — home` : 'Home'}
          className={`flex items-center gap-2.5 min-w-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${collapsed ? 'group-hover:opacity-0 transition-opacity' : ''}`}
        >
          {firmLogo ? (
            <>
              <img
                src={firmLogo}
                alt={brand.name || ''}
                className={`${firmLogoSize} object-contain shrink-0`}
                onError={() => setFailedImages((failed) => [...failed, firmLogo])}
              />
              {!collapsed && brand.name && (
                <span className="flex flex-col leading-none min-w-0">
                  <span className="text-sm font-bold tracking-tight truncate">{brand.name}</span>
                </span>
              )}
            </>
          ) : (
            <>
              {brand?.logoUrl && (
                <img src={brand.logoUrl} alt={brand.name || ''} className="w-8 h-8 object-contain shrink-0 brightness-0 invert" />
              )}
              {!collapsed && brand?.name && (
                <span className="flex flex-col leading-none min-w-0">
                  <span className="text-sm font-bold tracking-tight truncate">{brand.name}</span>
                </span>
              )}
            </>
          )}
        </Link>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={!collapsed}
          aria-controls="app-sidebar-nav"
          aria-keyshortcuts="Meta+B Control+B"
          title={`${collapsed ? 'Expand' : 'Collapse'} sidebar (Ctrl/⌘ B)`}
          className={`flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 shrink-0 ${
            collapsed
              ? 'absolute inset-0 m-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity'
              : 'ml-auto'
          }`}
        >
          <Glyph name="chevron" className={`transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Nav (scrollable) */}
      <div id="app-sidebar-nav" className="flex-1 overflow-y-auto overflow-x-hidden pb-2">
        {groups.map((group) => {
          const visibleItems = group.items.filter((item) => item.show)
          if (visibleItems.length === 0) return null

          return (
            <div key={group.key} className="mb-1">
              {group.label && (
                collapsed ? (
                  <div className="mx-3 my-2 border-t border-white/15" role="separator" aria-label={group.label} />
                ) : (
                  <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-primary-contrast/50">
                    {group.label}
                  </div>
                )
              )}
              <ul className="px-2">
                {visibleItems.map((item) => {
                  const active = isItemActive(item)
                  return (
                    <li key={item.to}>
                      {/* Plain Link, not NavLink: NavLink derives aria-current from its own
                          `to`, which silently drops the attribute on prefix-matched items
                          (Payment Processing links to /payments/daily-processing but owns the
                          whole /payments tree). isItemActive is the single source of truth for
                          both the styling and the announcement. */}
                      <Link
                        to={item.to}
                        title={collapsed ? item.text : undefined}
                        aria-label={collapsed ? item.text : undefined}
                        aria-current={active ? 'page' : undefined}
                        className={`relative flex items-center gap-3 rounded-lg px-3 py-2 my-0.5 text-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                          active ? 'bg-white/15 font-semibold' : 'text-primary-contrast/85 hover:bg-white/10 hover:text-primary-contrast'
                        } ${collapsed ? 'justify-center' : ''}`}
                      >
                        {active && (
                          <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-primary-contrast" aria-hidden="true" />
                        )}
                        <Glyph name={item.icon} className="shrink-0" />
                        {!collapsed && <span className="truncate">{item.text}</span>}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </div>

      {/* Account footer. Menu opens upward so it never clips the viewport bottom. */}
      <div className="shrink-0 border-t border-white/15 p-2 relative">
        <button
          type="button"
          onClick={openMenu}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`${displayName}, ${roleLabel} — account menu`}
          className={`flex items-center w-full rounded-lg hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
            collapsed ? 'justify-center p-1.5' : 'gap-2.5 px-2 py-1.5'
          }`}
        >
          <img src={noAvatar} alt="" className="w-8 h-8 rounded-full shrink-0 object-cover" />
          {!collapsed && (
            <>
              <span className="flex flex-col leading-tight min-w-0 text-left">
                <span className="text-sm font-semibold truncate">{displayName}</span>
                <span className="text-xs text-primary-contrast/65 truncate">{roleLabel}</span>
              </span>
              <Glyph name="caret" className="ml-auto shrink-0 opacity-70" />
            </>
          )}
        </button>
        <PopupWithActions anchor={menuAnchor} open={menuOpen} handleClose={closeMenu} placement="top-start">
          <div className="min-w-[210px]">
            <div className="px-[14px] pt-3 pb-2.5 border-b border-divider">
              <div className="text-text-primary text-sm font-semibold truncate">{displayName}</div>
              <div className="text-text-secondary text-xs truncate">
                {roleLabel}{user?.email ? ` · ${user.email}` : ''}
              </div>
            </div>
            {offices.length > 1 && (
              <div className="px-[14px] pt-3 pb-2.5 border-b border-divider">
                <div className="text-text-secondary text-xs mb-1.5">Office</div>
                {offices.map((office) => (
                  <div
                    key={office._id}
                    onClick={() => switchOffice(office._id)}
                    className={`cursor-pointer rounded-md px-2 py-1.5 text-sm hover:bg-surface ${
                      office._id === activeOfficeId ? 'text-text-primary font-semibold' : 'text-text-secondary'
                    }`}
                  >
                    {office.name}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between p-[14px] border-b border-divider">
              <span className="text-text-primary text-sm font-medium">Dark Mode</span>
              <Switch
                checked={mode === 'dark'}
                onChange={toggleColorMode}
                size="small"
                color="primary"
              />
            </div>
            {showEmailSettings && (
              <button
                type="button"
                onClick={() => setEmailSettingsOpen(true)}
                className="w-full text-left cursor-pointer p-[14px] rounded-md text-text-primary hover:bg-surface text-sm border-b border-divider"
              >
                Email notifications
              </button>
            )}
            {/* Multi-brand. Shown only to someone who actually holds more than one firm, which is
                nobody today. Without it the picker is reachable only through a server refusal, so
                the first choice a two-firm user makes is the last one they can make. */}
            {(user?.availableCompanies?.length || 0) > 1 && (
              <div
                onClick={() => window.location.assign(SELECT_COMPANY_PATH)}
                className="cursor-pointer p-[14px] rounded-md text-text-primary hover:bg-surface text-sm border-b border-divider"
              >
                Switch brand
              </div>
            )}
            <div
              onClick={logout}
              className="cursor-pointer p-[14px] rounded-md text-text-primary hover:bg-surface text-sm"
            >
              Logout
            </div>
          </div>
        </PopupWithActions>
        {emailSettingsOpen && <EmailSettingsDialog onClose={() => setEmailSettingsOpen(false)} />}
      </div>
    </nav>
  )
}

export default AppSidebar
